// Release A — Admin Marketing Inquiries inbox.
//
// Visual admin layer on top of the Phase 1 /api/admin/marketing/inquiries
// endpoints. Lets an authorized master_admin triage inquiries, assign
// statuses, add private notes, and inspect email-delivery results.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Inbox, AlertCircle, Search, RotateCw, CheckCircle2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { AdminCreatorModeration } from "@/components/marketing/AdminCreatorModeration";

const STATUSES = [
  { v: "", l: "All" },
  { v: "new", l: "New" },
  { v: "reviewing", l: "Reviewing" },
  { v: "contacted", l: "Contacted" },
  { v: "approved", l: "Approved" },
  { v: "declined", l: "Declined" },
  { v: "completed", l: "Completed" },
];
const AUDIENCE_FILTERS = [
  { v: "", l: "All types" },
  { v: "business", l: "Businesses" },
  { v: "affiliate", l: "Affiliates" },
  { v: "creator", l: "Creators" },
  { v: "social_promotion", l: "Social Promotion" },
];
const STATUS_CLS = {
  new: "bg-amber-500/15 text-amber-300 border-amber-400/40",
  reviewing: "bg-sky-500/15 text-sky-300 border-sky-400/40",
  contacted: "bg-indigo-500/15 text-indigo-300 border-indigo-400/40",
  qualified: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  closed: "bg-white/5 text-white/60 border-white/20",
  approved: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  declined: "bg-rose-500/15 text-rose-300 border-rose-400/40",
  completed: "bg-white/5 text-white/60 border-white/20",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminMarketingInquiries() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("");
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("inquiries");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (audienceFilter) params.set("audience_type", audienceFilter);
      const { data } = await api.get(`/admin/marketing/inquiries?${params}`);
      setList(data.inquiries || []);
      setCounts(data.audience_counts || {});
    } catch (e) {
      setErr(apiErrorMessage(e, "Could not load inquiries"));
      setList([]);
    } finally { setLoading(false); }
  }, [statusFilter, audienceFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((i) =>
      [i.contact_name, i.brand_name, i.creator_name, i.email, i.product_category, i.goals]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q)));
  }, [list, query]);

  async function patch(id, body) {
    setSavingId(id);
    try {
      await api.patch(`/admin/marketing/inquiries/${id}`, body);
      await load();
      if (selected?.id === id) {
        setSelected((s) => ({ ...s, ...body }));
      }
    } catch (e) {
      setErr(apiErrorMessage(e, "Save failed"));
    } finally { setSavingId(""); }
  }

  return (
    <div className="p-4 lg:p-6 min-h-screen bg-[#06080b] text-white" data-testid="admin-marketing-inquiries">
      <div className="flex items-center gap-3 mb-6">
        <Inbox className="h-6 w-6 text-lifted-green" />
        <h1 className="font-anton uppercase text-2xl text-white">Marketing Inquiries</h1>
        <button onClick={load} data-testid="refresh-inquiries"
          className="ml-auto px-3 py-1.5 border border-white/15 rounded text-xs hover:bg-white/5 inline-flex items-center gap-1">
          <RotateCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-5" role="tablist">
        {[["inquiries", "Inquiries"], ["creators", "Creator Showcase"]].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`admin-mkt-tab-${k}`}
            className={`text-xs font-bebas uppercase tracking-[0.2em] px-4 py-2 rounded border ${tab === k ? "bg-lifted-green text-black border-lifted-green" : "border-white/15 text-white/70"}`}>{l}</button>
        ))}
      </div>
      {tab === "creators" ? <AdminCreatorModeration /> : (<>
      <div className="flex gap-1 flex-wrap mb-3" data-testid="audience-filters">
        {AUDIENCE_FILTERS.map((a) => (
          <button key={a.v || "all"} onClick={() => setAudienceFilter(a.v)} data-testid={`audience-filter-${a.v || "all"}`}
            className={`text-[10px] font-bebas uppercase tracking-[0.2em] px-3 py-1.5 rounded border ${
              audienceFilter === a.v ? "bg-lifted-green text-black border-lifted-green" : "border-white/15 text-white/70 hover:text-white"}`}>
            {a.l}{a.v && counts[a.v] !== undefined ? ` (${counts[a.v]})` : ""}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-center mb-5">
        <div className="flex gap-1 bg-black/40 border border-white/10 rounded p-1">
          {STATUSES.map((s) => (
            <button key={s.v || "all"} onClick={() => setStatusFilter(s.v)}
              data-testid={`status-filter-${s.v || "all"}`}
              className={`text-[10px] font-bebas uppercase tracking-[0.2em] px-3 py-1.5 rounded ${
                statusFilter === s.v ? "bg-lifted-green text-black" : "text-white/70 hover:text-white"
              }`}>{s.l}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-3 w-3 absolute left-3 top-3 text-white/50" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, brand, email, goals…"
            data-testid="inquiry-search"
            className="input-lifted w-full pl-8 text-sm" />
        </div>
        <span className="text-xs text-white/50 ml-auto">
          {filtered.length} of {list.length}
        </span>
      </div>

      {err && (
        <div className="glass-panel p-3 mb-4 border border-rose-500/40 text-rose-200 text-sm" data-testid="inquiries-error">
          {err}
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm" data-testid="inquiries-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-panel p-8 text-center" data-testid="inquiries-empty">
          <AlertCircle className="h-8 w-8 text-lifted-green mx-auto mb-3" />
          <div className="text-white font-anton uppercase text-xl">No inquiries</div>
          <p className="text-white/50 text-xs mt-2">
            Nothing matches these filters. Marketing inquiries submitted at
            /lifted-marketing appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 items-start">
          <div className="glass-panel overflow-hidden">
            <table className="w-full text-xs" data-testid="inquiries-table">
              <thead className="bg-black/40 text-white/60">
                <tr>
                  <th className="text-left px-3 py-2">Received</th>
                  <th className="text-left px-3 py-2">Brand / Contact</th>
                  <th className="text-left px-3 py-2">Audience</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} onClick={() => setSelected(i)}
                    data-testid={`inquiry-row-${i.id}`}
                    className={`border-t border-white/5 cursor-pointer hover:bg-white/5 ${
                      selected?.id === i.id ? "bg-lifted-green/5" : ""
                    }`}>
                    <td className="px-3 py-2 text-white/60">{fmt(i.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="text-white">{i.brand_name || i.creator_name || i.contact_name}</div>
                      <div className="text-white/50 text-[11px]">{i.contact_name}</div>
                    </td>
                    <td className="px-3 py-2 capitalize text-white/70">{(i.audience_type || "business").replace("_", " ")}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded border text-[10px] font-bebas uppercase tracking-wider ${STATUS_CLS[i.status] || STATUS_CLS.new}`}>
                        {i.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-white/60 flex items-center gap-1">
                      {i.notified_at
                        ? <><CheckCircle2 className="h-3 w-3 text-lifted-green" /> sent</>
                        : i.notification_error
                          ? <><AlertCircle className="h-3 w-3 text-amber-400" /> {i.notification_error.slice(0, 24)}</>
                          : <span className="text-white/30">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside className="glass-panel p-4 lg:sticky lg:top-4" data-testid="inquiry-detail">
            {!selected ? (
              <div className="text-white/50 text-xs text-center py-10">
                Select an inquiry on the left to review and respond.
              </div>
            ) : (
              <>
                <div className="eyebrow text-lifted-green">Inquiry Details</div>
                <h3 className="font-anton uppercase text-xl mt-1" data-testid="inquiry-detail-brand">
                  {selected.brand_name || selected.creator_name || selected.contact_name}
                </h3>
                <div className="text-xs text-white/70 mt-1">
                  {selected.contact_name} · <a className="text-lifted-green hover:underline"
                    href={`mailto:${selected.email}`} data-testid="inquiry-detail-email">
                    {selected.email}
                  </a>
                </div>
                {selected.website_or_social && (
                  <div className="text-[11px] text-white/50 mt-1 truncate">{selected.website_or_social}</div>
                )}
                <dl className="mt-4 space-y-2 text-xs">
                  <Row k="Audience" v={(selected.audience_type || "business").replace("_", " ")} />
                  <Row k="Type" v={selected.inquiry_type} />
                  <Row k="Phone" v={selected.phone || "—"} />
                  <Row k="Location" v={selected.location || "—"} />
                  <Row k="Category" v={selected.product_category || "—"} />
                  <Row k="Budget" v={selected.budget_range || "—"} />
                  <Row k="Timing" v={selected.desired_timing || "—"} />
                  <Row k="Submitted" v={fmt(selected.created_at)} />
                </dl>
                <CreatorDetails i={selected} />
                <div className="mt-4">
                  <div className="eyebrow text-white/50 mb-1">Goals</div>
                  <p className="text-sm text-white/80 whitespace-pre-wrap bg-black/40 border border-white/10 rounded p-3 max-h-48 overflow-y-auto">
                    {selected.goals}
                  </p>
                </div>
                <div className="mt-4">
                  <div className="eyebrow text-white/50 mb-1">Change status</div>
                  <div className="flex flex-wrap gap-1">
                    {STATUSES.filter((s) => s.v).map((s) => (
                      <button key={s.v}
                        disabled={savingId === selected.id || selected.status === s.v}
                        onClick={() => patch(selected.id, { status: s.v })}
                        data-testid={`set-status-${s.v}`}
                        className={`text-[10px] font-bebas uppercase tracking-wider px-2 py-1 rounded border ${
                          selected.status === s.v
                            ? "border-lifted-green bg-lifted-green/10 text-lifted-green"
                            : "border-white/20 text-white/70 hover:border-lifted-green"
                        } disabled:opacity-50`}>{s.l}</button>
                    ))}
                  </div>
                </div>
                <AdminNotes
                  initialValue={selected.notes || ""}
                  onSave={(notes) => patch(selected.id, { notes })}
                  saving={savingId === selected.id}
                />
                <div className="mt-4 text-[10px] text-white/40 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {selected.notified_at
                    ? `Admin notified ${fmt(selected.notified_at)}`
                    : selected.notification_error
                      ? `Notify error: ${selected.notification_error}`
                      : "Pending notification…"}
                </div>
              </>
            )}
          </aside>
        </div>
      )}
      </>)}
    </div>
  );
}

function CreatorDetails({ i }) {
  if (!i.audience_type || i.audience_type === "business") return null;
  return (
    <div className="mt-4 space-y-2 text-xs" data-testid="inquiry-creator-details">
      <Row k="Creator name" v={i.creator_name || "—"} />
      <Row k="Niche" v={i.content_niche || "—"} />
      <Row k="Primary" v={i.primary_platform || "—"} />
      <Row k="Affiliate ID" v={i.affiliate_id_submitted ? `${i.affiliate_id_submitted} (${i.affiliate_id_valid ? "valid" : "not found"})` : "—"} />
      <Row k="Contact via" v={i.preferred_contact || "—"} />
      <Row k="Public listing" v={i.list_publicly ? "Opted in (pending review)" : "No"} />
      <div className="flex flex-wrap gap-1 pt-1">
        {(i.social_links || []).map((s) => (
          <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="px-2 py-0.5 border border-white/15 rounded text-lifted-green hover:underline">
            {s.platform}{i.follower_counts?.[s.platform] ? ` · ${Number(i.follower_counts[s.platform]).toLocaleString()} (self-reported)` : ""}
          </a>
        ))}
      </div>
      {i.services?.length > 0 && <div className="text-white/60">Requested: {i.services.join(", ").replaceAll("_", " ")}</div>}
      {i.message && <p className="text-white/70 whitespace-pre-wrap bg-black/40 border border-white/10 rounded p-2">{i.message}</p>}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-white/50 uppercase font-bebas tracking-[0.18em] text-[10px]">{k}</dt>
      <dd className="text-white truncate max-w-[60%]">{v}</dd>
    </div>
  );
}

function AdminNotes({ initialValue, onSave, saving }) {
  const [v, setV] = useState(initialValue);
  useEffect(() => setV(initialValue), [initialValue]);
  const dirty = v !== initialValue;
  return (
    <div className="mt-4">
      <div className="eyebrow text-white/50 mb-1">Private notes</div>
      <textarea value={v} onChange={(e) => setV(e.target.value)}
        data-testid="inquiry-notes"
        className="input-lifted w-full text-sm min-h-[90px]"
        placeholder="Context for the team…" />
      <button disabled={!dirty || saving} onClick={() => onSave(v)}
        data-testid="inquiry-notes-save"
        className="btn-lifted text-xs mt-2 disabled:opacity-50">
        {saving ? "Saving…" : "Save notes"}
      </button>
    </div>
  );
}
